import React, { useEffect, useState } from "react";
import { auth } from "./firebase";
import { Navigate } from "react-router-dom";
import {
  collection,
  getDocs,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { db } from "./firebase";
import { Card } from "primereact/card";
import { DataView } from "primereact/dataview";
import { TabView, TabPanel } from "primereact/tabview";
import { ProgressSpinner } from "primereact/progressspinner";
import "./styles/Admin.css";

const ADMIN_EMAIL = "clys@codepower.biz";

interface UserData {
  uid: string;
  email: string;
  displayName?: string;
  plan?: string;
  lastActive?: Timestamp;
  createdAt?: Timestamp;
  modifiedAt?: Timestamp;
}

const Admin: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<UserData[]>([]);
  const [activeUsers1Day, setActiveUsers1Day] = useState<UserData[]>([]);
  const [activeUsers7Days, setActiveUsers7Days] = useState<UserData[]>([]);
  const [activeUsers14Days, setActiveUsers14Days] = useState<UserData[]>([]);
  const [activeUsers30Days, setActiveUsers30Days] = useState<UserData[]>([]);
  const [customers, setCustomers] = useState<UserData[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  const currentUser = auth.currentUser;

  // Check if user is admin
  if (!currentUser || currentUser.email !== ADMIN_EMAIL) {
    return <Navigate to="/" />;
  }

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);

        // Fetch all users
        const usersRef = collection(db, "users");
        const usersSnapshot = await getDocs(usersRef);

        const allUsers: UserData[] = [];
        usersSnapshot.forEach((doc) => {
          const data = doc.data();
          allUsers.push({
            uid: doc.id,
            email: data.email || "",
            displayName: data.displayName || data.email || "Unknown",
            plan: data.plan || "free",
            lastActive: data.lastActive,
            createdAt: data.createdAt,
            modifiedAt: data.modifiedAt,
          });
        });

        // Sort all users by modifiedAt desc
        const sortedUsers = allUsers.sort((a, b) => {
          const aDate = a.modifiedAt?.toDate().getTime() || 0;
          const bDate = b.modifiedAt?.toDate().getTime() || 0;
          return bDate - aDate;
        });

        setUsers(sortedUsers);

        // Debug: Log users with lastActive data
        console.log("📊 Total users:", allUsers.length);
        console.log(
          "📊 Users with lastActive:",
          allUsers.filter((u) => u.lastActive).length
        );
        console.log(
          "📊 Sample user data:",
          allUsers.find((u) => u.email === "clys@codepower.biz")
        );

        // Calculate date thresholds
        const now = new Date();
        const oneDayAgo = new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000);
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const fourteenDaysAgo = new Date(
          now.getTime() - 14 * 24 * 60 * 60 * 1000
        );
        const thirtyDaysAgo = new Date(
          now.getTime() - 30 * 24 * 60 * 60 * 1000
        );

        // Filter active users (using modifiedAt if lastActive is not available)
        const users1Day = allUsers
          .filter((user) => {
            const activityDate = user.lastActive || user.modifiedAt;
            if (!activityDate) return false;
            const lastActiveDate = activityDate.toDate();
            return lastActiveDate >= oneDayAgo;
          })
          .sort((a, b) => {
            const aDate = a.modifiedAt?.toDate().getTime() || 0;
            const bDate = b.modifiedAt?.toDate().getTime() || 0;
            return bDate - aDate;
          });

        const users7Days = allUsers
          .filter((user) => {
            const activityDate = user.lastActive || user.modifiedAt;
            if (!activityDate) return false;
            const lastActiveDate = activityDate.toDate();
            return lastActiveDate >= sevenDaysAgo;
          })
          .sort((a, b) => {
            const aDate = a.modifiedAt?.toDate().getTime() || 0;
            const bDate = b.modifiedAt?.toDate().getTime() || 0;
            return bDate - aDate;
          });

        const users14Days = allUsers
          .filter((user) => {
            const activityDate = user.lastActive || user.modifiedAt;
            if (!activityDate) return false;
            const lastActiveDate = activityDate.toDate();
            return lastActiveDate >= fourteenDaysAgo;
          })
          .sort((a, b) => {
            const aDate = a.modifiedAt?.toDate().getTime() || 0;
            const bDate = b.modifiedAt?.toDate().getTime() || 0;
            return bDate - aDate;
          });

        const users30Days = allUsers
          .filter((user) => {
            const activityDate = user.lastActive || user.modifiedAt;
            if (!activityDate) return false;
            const lastActiveDate = activityDate.toDate();
            return lastActiveDate >= thirtyDaysAgo;
          })
          .sort((a, b) => {
            const aDate = a.modifiedAt?.toDate().getTime() || 0;
            const bDate = b.modifiedAt?.toDate().getTime() || 0;
            return bDate - aDate;
          });

        // Filter customers (non-free plans) and sort by modifiedAt desc
        const paidCustomers = allUsers
          .filter((user) => user.plan && user.plan.toLowerCase() !== "free")
          .sort((a, b) => {
            const aDate = a.modifiedAt?.toDate().getTime() || 0;
            const bDate = b.modifiedAt?.toDate().getTime() || 0;
            return bDate - aDate; // desc order
          });

        setActiveUsers1Day(users1Day);
        setActiveUsers7Days(users7Days);
        setActiveUsers14Days(users14Days);
        setActiveUsers30Days(users30Days);
        setCustomers(paidCustomers);

        setLoading(false);
      } catch (error) {
        console.error("Error fetching user data:", error);
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const formatDate = (timestamp?: Timestamp) => {
    if (!timestamp) return "Not set";
    const date = timestamp.toDate();
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const userItemTemplate = (user: UserData) => {
    return (
      <div className="admin-user-item">
        <div className="user-info">
          <div className="user-name">{user.displayName}</div>
          <div className="user-email">{user.email}</div>
          {user.plan && user.plan !== "free" && (
            <div className="user-plan">Plan: {user.plan}</div>
          )}
        </div>
        <div className="user-dates">
          <div className="user-created">
            Created: {formatDate(user.createdAt)}
          </div>
          <div className="user-modified">
            Modified: {formatDate(user.modifiedAt)}
          </div>
        </div>
      </div>
    );
  };

  // Filter users by email search query
  const filterUsersByEmail = (userList: UserData[]) => {
    if (!searchQuery.trim()) return userList;
    const query = searchQuery.toLowerCase();
    return userList.filter((user) => user.email.toLowerCase().includes(query));
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <ProgressSpinner />
        <p>Loading admin dashboard...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <p>Welcome, {currentUser.email}</p>
      </div>

      <div className="admin-stats-grid">
        <Card title="Total Users" className="admin-stat-card">
          <div className="stat-number">{users.length}</div>
        </Card>
        <Card title="Customers" className="admin-stat-card">
          <div className="stat-number">{customers.length}</div>
        </Card>
        <Card title="Active (7 days)" className="admin-stat-card">
          <div className="stat-number">{activeUsers7Days.length}</div>
        </Card>
        <Card title="Active (30 days)" className="admin-stat-card">
          <div className="stat-number">{activeUsers30Days.length}</div>
        </Card>
      </div>

      <div className="admin-content">
        <Card
          title={
            <div className="active-users-header">
              <span>Active Users ({activeUsers1Day.length} in last 24h)</span>
              <div className="search-field">
                <input
                  type="text"
                  placeholder="Search by email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="email-search-input"
                />
              </div>
            </div>
          }
          className="admin-card"
        >
          <TabView>
            <TabPanel header={`1 Day (${activeUsers1Day.length})`}>
              <DataView
                value={filterUsersByEmail(activeUsers1Day)}
                itemTemplate={userItemTemplate}
                paginator
                rows={10}
                emptyMessage="No active users in the last 24 hours"
              />
            </TabPanel>
            <TabPanel header={`7 Days (${activeUsers7Days.length})`}>
              <DataView
                value={filterUsersByEmail(activeUsers7Days)}
                itemTemplate={userItemTemplate}
                paginator
                rows={10}
                emptyMessage="No active users in the last 7 days"
              />
            </TabPanel>
            <TabPanel header={`14 Days (${activeUsers14Days.length})`}>
              <DataView
                value={filterUsersByEmail(activeUsers14Days)}
                itemTemplate={userItemTemplate}
                paginator
                rows={10}
                emptyMessage="No active users in the last 14 days"
              />
            </TabPanel>
            <TabPanel header={`30 Days (${activeUsers30Days.length})`}>
              <DataView
                value={filterUsersByEmail(activeUsers30Days)}
                itemTemplate={userItemTemplate}
                paginator
                rows={10}
                emptyMessage="No active users in the last 30 days"
              />
            </TabPanel>
            <TabPanel header={`All (${users.length})`}>
              <DataView
                value={filterUsersByEmail(users)}
                itemTemplate={userItemTemplate}
                paginator
                rows={10}
                emptyMessage="No users found"
              />
            </TabPanel>
          </TabView>
        </Card>

        <Card
          title={`Customers (${customers.length})`}
          className="admin-card customers-card"
        >
          <DataView
            value={customers}
            itemTemplate={userItemTemplate}
            paginator
            rows={10}
            emptyMessage="No customers found"
          />
        </Card>
      </div>
    </div>
  );
};

export default Admin;
